import { trpc } from '@/lib/api/trpc';
import { toast } from 'sonner';

/**
 * Hook to retry a failed billing report
 */
export function useRetryReport(onSuccess?: () => void) {
  return trpc.billingReports.retry.useMutation({
    onSuccess: () => {
      toast.success('Billing retry started successfully');
      if (onSuccess) {
        setTimeout(onSuccess, 3000);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to retry report');
    }
  });
}

/**
 * Hook to delete a billing report
 */
export function useDeleteReport(onSuccess?: () => void) {
  return trpc.accountManager.deleteBillingReport.useMutation({
    onSuccess: () => {
      toast.success('Billing report deleted successfully');
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete report');
    }
  });
}

/**
 * Hook to resolve a report's invoice links against Stripe at click time.
 *
 * No onSuccess/onError, unlike its siblings: the caller holds the tab it opened
 * before awaiting, and is the only one that can point it at the fresh URL or
 * close it on failure.
 */
export function useInvoiceLink() {
  return trpc.billingReports.getInvoiceLink.useMutation();
}

/**
 * Hook to update billing email
 */
export function useUpdateBillingEmail(onSuccess?: () => void) {
  return trpc.accountPlans.updateBillingEmail.useMutation({
    onSuccess: () => {
      toast.success('Billing email updated successfully');
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update billing email');
    }
  });
}

/**
 * Hook to open Stripe Customer Portal (full billing portal)
 * Redirects user to Stripe's hosted billing portal
 */
export function useCustomerPortal() {
  return trpc.stripe.createCustomerPortalSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to open customer portal');
    }
  });
}

/**
 * Hook to open the provider's hosted card management page (Stripe Customer
 * Portal). Providers without one (Checkout.com) return no url: the CRM
 * form takes over in the next rollout step.
 */
export function usePaymentPortal() {
  return trpc.billing.getManagementUrl.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('Card management is not available yet on this billing provider. Please contact support.');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to open payment portal');
    }
  });
}
